import { cn } from "@/lib/cn";
import { CropMarks } from "@/components/common/CropMarks";
import { Reveal } from "@/components/common/Reveal";
import { useBackground } from "@/contexts/BackgroundContext";
import type { ReactNode } from "react";

interface ScrollSectionProps {
  id: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
  compact?: boolean;
}

export function ScrollSection({ id, title, subtitle, children, className, compact }: ScrollSectionProps) {
  const { overlayOpacity } = useBackground();

  const overlayColor = `rgba(var(--color-overlay-rgb),${overlayOpacity})`;

  return (
    <section
      id={id}
      className={cn("relative flex flex-col justify-center py-24 px-8 z-10 backdrop-blur-[2px]", className)}
      style={{
        minHeight: compact ? undefined : "var(--section-min-h)",
        background: overlayColor,
        transition: "background 1.5s ease-in-out",
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-32 -translate-y-full pointer-events-none"
        style={{
          background: `linear-gradient(to bottom, transparent, ${overlayColor})`,
          transition: "background 1.5s ease-in-out",
        }}
        aria-hidden="true"
      />
      <CropMarks />
      <div className="max-w-6xl mx-auto w-full">
        <Reveal className="mb-12">
          <h2 className="text-3xl md:text-5xl font-light tracking-tight mb-4 font-[family-name:var(--font-display)]">
            {title}
          </h2>
          {subtitle && (
            <p className="text-lg text-[var(--color-fg-secondary)] max-w-xl font-light">
              {subtitle}
            </p>
          )}
        </Reveal>
        <Reveal delay={100}>
          {children}
        </Reveal>
      </div>
    </section>
  );
}
